import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { api } from '../api/client';
import Inspector from './Inspector';
import ContentEditor from './ContentEditor';
import AiRewriteDialog from './AiRewriteDialog';
import ImagePromptDialog from './ImagePromptDialog';
const TABS = [
    { id: 'settings', label: 'Settings', icon: '⚙' },
    { id: 'content', label: 'Content', icon: '✎' },
    { id: 'ai', label: 'AI', icon: '✨' },
    { id: 'code', label: 'Code', icon: '{ }' },
    { id: 'history', label: 'History', icon: '↺' },
];
const STORAGE_KEY = 'ssv2:editor:insTab';
export default function InspectorTabs({ slug, block, inlineEditingBlockId, onSettingsChange, contentValue, onContentChange, onSettingsSave, onOpenCustomCode, onContentSaved, forceTab, }) {
    const [tab, setTab] = useState(() => {
        try {
            const saved = localStorage.getItem(STORAGE_KEY);
            if (saved && TABS.some((t) => t.id === saved))
                return saved;
        }
        catch { /* localStorage might be disabled */ }
        return 'settings';
    });
    function pick(id) {
        setTab(id);
        try {
            localStorage.setItem(STORAGE_KEY, id);
        }
        catch { /* noop */ }
    }
    const hasContent = !!block.content;
    // Auto-switch to Content if a block with no settings was just selected and
    // the prior tab was Content. Otherwise keep the last-used tab.
    useEffect(() => {
        if (tab === 'content' && !hasContent)
            setTab('settings');
    }, [block.id, hasContent]); // eslint-disable-line react-hooks/exhaustive-deps
    // Phase F push 3: respond to a parent-driven tab nudge (e.g. the iframe
    // toolbar's "{ } Source" button). The nonce ensures we react to repeats.
    useEffect(() => {
        if (!forceTab)
            return;
        if (forceTab.blockId !== block.id)
            return;
        if (forceTab.tab === 'content' && !hasContent)
            return;
        pick(forceTab.tab);
    }, [forceTab?.nonce]); // eslint-disable-line react-hooks/exhaustive-deps
    return (_jsxs("div", { className: "ins-tabs", children: [_jsx("div", { className: "ins-tabs-head", children: _jsxs("div", { className: "ins-tabs-title", children: [_jsxs("strong", { children: [block.type, block.layout ? `:${block.layout}` : ''] }), _jsxs("span", { className: "muted small", children: ["#", block.id] })] }) }), _jsx("nav", { className: "ins-tabs-strip", role: "tablist", children: TABS.map((t) => {
                    const disabled = t.id === 'content' && !hasContent;
                    return (_jsxs("button", { role: "tab", "aria-selected": tab === t.id, className: tab === t.id ? 'ins-tab active' : 'ins-tab', disabled: disabled, onClick: () => pick(t.id), title: disabled ? 'No content file on this block' : t.label, children: [_jsx("span", { className: "ins-tab-icon", children: t.icon }), _jsx("span", { className: "ins-tab-label", children: t.label })] }, t.id));
                }) }), _jsxs("div", { className: "ins-tabs-body", children: [tab === 'settings' && (_jsx(Inspector, { block: block, onChange: onSettingsChange, onSave: onSettingsChange ? undefined : onSettingsSave, onOpenCustomCode: () => pick('code') })), tab === 'content' && hasContent && (_jsx(ContentEditor, { slug: slug, blockId: block.id, blockType: block.type, value: contentValue, onChange: onContentChange, onSaved: onContentSaved, inlineLocked: inlineEditingBlockId === block.id }, `content-${block.id}`)), tab === 'ai' && (_jsx(AiTabBody, { slug: slug, block: block, onContentSaved: onContentSaved })), tab === 'code' && (_jsx(CodeTabBody, { block: block, onOpen: onOpenCustomCode })), tab === 'history' && (_jsx(HistoryTabBody, { slug: slug, reloadKey: block.id }))] })] }));
}
// ---------- AI tab ---------------------------------------------------
const IMPROVE_DEFAULT = 'Improve this content for clarity, accessibility, and conversion. Tighten copy, sharpen the call to action, and keep all media tokens / classes / links exactly as they appear.';
const WRAPPER_TYPES_AI = new Set(['section', 'container', 'row', 'column']);
function AiTabBody({ slug, block, onContentSaved }) {
    const [improveDialog, setImproveDialog] = useState(null);
    const [imageDialog, setImageDialog] = useState(false);
    const [busy, setBusy] = useState(false);
    const [note, setNote] = useState(null);
    const [sectionPreview, setSectionPreview] = useState(null);
    const hasContent = !!block.content;
    const isWrapper = WRAPPER_TYPES_AI.has(block.type);
    async function openImprove() {
        if (!hasContent)
            return;
        setBusy(true);
        setNote(null);
        try {
            const { html } = await api.getContent(slug, block.id);
            if (!html.trim()) {
                setNote('Block content is empty — nothing to improve yet.');
                return;
            }
            setImproveDialog({ initialHtml: html });
        }
        catch (e) {
            setNote(e?.message ?? 'Could not load block content');
        }
        finally {
            setBusy(false);
        }
    }
    async function applyImprove(newHtml) {
        setBusy(true);
        setNote(null);
        try {
            await api.putContent(slug, block.id, newHtml);
            setImproveDialog(null);
            onContentSaved?.();
            setNote('Saved improved content.');
            setTimeout(() => setNote((n) => n === 'Saved improved content.' ? null : n), 1800);
        }
        catch (e) {
            setNote(e?.message ?? 'Save failed');
        }
        finally {
            setBusy(false);
        }
    }
    async function runSectionAction(intent) {
        if (isWrapper) {
            setNote('Pick a content block inside this section first.');
            return;
        }
        let instruction = '';
        if (intent === 'translate') {
            instruction = window.prompt('Translate to which language?', 'French') ?? '';
            if (!instruction.trim())
                return;
        }
        else if (intent === 'redesign') {
            instruction = window.prompt('Optional: describe the direction (e.g. "make it bolder, asymmetric layout"). Leave blank for free-hand.', '') ?? '';
        }
        setBusy(true);
        setNote(`Generating (${intent})…`);
        try {
            const r = await api.aiSection({ slug, block_id: block.id, intent, instruction, apply: false });
            setSectionPreview({ intent, html: r.html, css: r.css, js: r.js, notes: r.notes });
            setNote(null);
        }
        catch (e) {
            setNote(e?.message ?? `${intent} failed`);
        }
        finally {
            setBusy(false);
        }
    }
    async function applySection() {
        if (!sectionPreview)
            return;
        setBusy(true);
        setNote(null);
        try {
            // Commit the EXACT previewed bytes — bypass /ai/section's apply mode
            // (which re-runs the AI and may drift). Write content + merge css/js
            // into the existing block settings.
            const newSettings = { ...(block.settings ?? {}) };
            if (sectionPreview.css)
                newSettings.custom_css = sectionPreview.css;
            else
                delete newSettings.custom_css;
            if (sectionPreview.js)
                newSettings.custom_js = sectionPreview.js;
            else
                delete newSettings.custom_js;
            await Promise.all([
                api.putContent(slug, block.id, sectionPreview.html),
                api.updateBlock(slug, block.id, { settings: newSettings }),
            ]);
            onContentSaved?.();
            setSectionPreview(null);
            setNote('Applied.');
            setTimeout(() => setNote((n) => n === 'Applied.' ? null : n), 1500);
        }
        catch (e) {
            setNote(e?.message ?? 'Apply failed');
        }
        finally {
            setBusy(false);
        }
    }
    return (_jsxs("div", { className: "tab-body padded", children: [isWrapper ? (_jsxs("p", { className: "muted small", style: { marginTop: 0 }, children: [_jsx("strong", { children: block.type }), " is a layout wrapper. Pick a content block (heading, text, image, \u2026) inside it to use AI tools, or use the ", _jsx("code", { children: "\uFF0B" }), " handles in the canvas to add columns / rows / blocks."] })) : (_jsxs("p", { className: "muted small", style: { marginTop: 0 }, children: ["AI actions for this ", _jsx("strong", { children: block.type }), " block. Each action returns a preview before applying."] })), _jsxs("div", { className: "ai-tools", style: { flexDirection: 'column', alignItems: 'stretch' }, children: [_jsx("button", { type: "button", className: "ghost", disabled: !hasContent || busy, onClick: openImprove, title: hasContent ? 'Quick rewrite using your custom instruction' : 'This block has no content file to improve.', children: "\uD83E\uDE84 Improve content" }), _jsx("button", { type: "button", className: "ghost", disabled: !hasContent || busy || isWrapper, onClick: () => runSectionAction('redesign'), title: "Free-hand redesign: layout, typography, hierarchy. Uses Bootstrap container/row/col scaffolding.", children: "\uD83C\uDFA8 Redesign section" }), _jsx("button", { type: "button", className: "ghost", disabled: !hasContent || busy || isWrapper, onClick: () => runSectionAction('match_brand'), title: "Re-align colors, voice, typography with your brand guidelines.", children: "\u2728 Match brand" }), _jsx("button", { type: "button", className: "ghost", disabled: !hasContent || busy || isWrapper, onClick: () => runSectionAction('translate'), title: "Translate visible text. Structure + classes + media tokens preserved.", children: "\uD83C\uDF10 Translate" }), _jsx("button", { type: "button", className: "ghost", onClick: () => setImageDialog(true), title: "Build an image-generation prompt you can paste into your tool of choice", children: "\uD83C\uDFA8 Image prompt" }), note && _jsx("div", { className: "muted small ai-tools-note", children: note })] }), sectionPreview && (_jsxs("div", { className: "ai-section-preview", children: [_jsx("div", { className: "ai-section-preview-head", children: _jsxs("strong", { children: ["Preview (", sectionPreview.intent, ")"] }) }), sectionPreview.notes && _jsx("p", { className: "muted small", children: sectionPreview.notes }), _jsxs("details", { children: [_jsxs("summary", { className: "muted small", children: ["New HTML (", sectionPreview.html.length, " bytes)"] }), _jsx("pre", { className: "codeblock", children: sectionPreview.html })] }), sectionPreview.css && (_jsxs("details", { children: [_jsxs("summary", { className: "muted small", children: ["Scoped CSS (", sectionPreview.css.length, " bytes)"] }), _jsx("pre", { className: "codeblock", children: sectionPreview.css })] })), sectionPreview.js && (_jsxs("details", { children: [_jsxs("summary", { className: "muted small", children: ["Scoped JS (", sectionPreview.js.length, " bytes)"] }), _jsx("pre", { className: "codeblock", children: sectionPreview.js })] })), _jsxs("div", { className: "actions", style: { marginTop: '0.6rem' }, children: [_jsx("button", { className: "ghost", onClick: () => setSectionPreview(null), disabled: busy, children: "Discard" }), _jsx("button", { onClick: applySection, disabled: busy, children: busy ? 'Applying…' : 'Use this version' })] })] })), improveDialog && (_jsx(AiRewriteDialog, { initialHtml: improveDialog.initialHtml, blockType: block.type, initialPrompt: IMPROVE_DEFAULT, title: "\uD83E\uDE84 Improve content", onAccept: applyImprove, onClose: () => setImproveDialog(null) })), imageDialog && (_jsx(ImagePromptDialog, { initialDescription: `A ${block.type} block${block.layout ? ` (${block.layout} layout)` : ''}`, onClose: () => setImageDialog(false) }))] }));
}
// ---------- Code tab -------------------------------------------------
function CodeTabBody({ block, onOpen }) {
    const css = typeof block.settings?.custom_css === 'string' ? block.settings.custom_css : '';
    const js = typeof block.settings?.custom_js === 'string' ? block.settings.custom_js : '';
    return (_jsxs("div", { className: "tab-body padded", children: [_jsxs("p", { className: "muted small", style: { marginTop: 0 }, children: ["Block-scoped CSS is auto-prefixed with ", _jsxs("code", { children: ["[data-block-id=\"", block.id, "\"]"] }), ". JS receives ", _jsx("code", { children: "block" }), " (the DOM element) inside an IIFE."] }), _jsxs("div", { className: "code-summary", style: { flexDirection: 'column', alignItems: 'stretch', gap: '0.5rem' }, children: [_jsxs("div", { className: "muted small", children: ["CSS: ", css ? `${css.length}b · ${css.split('\n').length}L` : 'empty'] }), _jsxs("div", { className: "muted small", children: ["JS: ", js ? `${js.length}b · ${js.split('\n').length}L` : 'empty'] }), _jsx("button", { type: "button", onClick: onOpen, children: '{ } Open code editor' })] })] }));
}
function HistoryTabBody({ slug, reloadKey }) {
    const [rows, setRows] = useState(null);
    const [error, setError] = useState(null);
    useEffect(() => {
        let alive = true;
        api.pageRevisions(slug)
            .then((r) => alive && setRows(r.revisions))
            .catch((e) => alive && setError(e?.message ?? 'Failed to load revisions'));
        return () => { alive = false; };
    }, [slug, reloadKey]);
    if (error)
        return _jsx("div", { className: "tab-body padded", children: _jsx("div", { className: "err small", children: error }) });
    if (!rows)
        return _jsx("div", { className: "tab-body padded", children: _jsx("div", { className: "muted small", children: "Loading\u2026" }) });
    return (_jsxs("div", { className: "tab-body padded", children: [_jsx("p", { className: "muted small", style: { marginTop: 0 }, children: "Recent page revisions. Use Ctrl+Z / Ctrl+Shift+Z to step through them \u2014 every block edit, drag, or AI rewrite snapshots into this list." }), rows.length === 0
                ? _jsx("div", { className: "muted small", children: "No revisions yet on this page." })
                : (_jsx("ul", { className: "revision-list", children: rows.slice(0, 20).map((r) => (_jsxs("li", { children: [_jsxs("div", { className: "rev-head", children: [_jsxs("strong", { children: ["r", r.revision_no] }), _jsx("span", { className: "muted small", children: relTime(r.created_at) })] }), r.comment && _jsx("div", { className: "muted small", children: r.comment })] }, r.revision_no))) })), _jsx("p", { className: "muted small", style: { marginTop: '0.7rem' }, children: "One-click restore lands in the next polish pass." })] }));
}
function relTime(iso) {
    const t = Date.parse(iso.replace(' ', 'T') + 'Z');
    if (Number.isNaN(t))
        return iso;
    const diff = Math.round((Date.now() - t) / 1000);
    if (diff < 60)
        return diff + 's ago';
    if (diff < 3600)
        return Math.round(diff / 60) + 'm ago';
    if (diff < 86400)
        return Math.round(diff / 3600) + 'h ago';
    if (diff < 86400 * 7)
        return Math.round(diff / 86400) + 'd ago';
    return iso.slice(0, 10);
}
